import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react'
import {
  Box,
  ResponsiveBox,
  useResponsiveWrapperContext,
} from '@usap/uicomponents'
import { Pagination } from '@usap/widgets'
import { Hydrator } from '@usap/core'
import seoIDs from '../../../config/seoIds'
import { useCartContext } from '../../contexts/cart.context'
import { useVehicleContext } from '../../contexts/vehicle.context'
import { useTokenContext, useTokenDispatch } from '../../contexts/token.context'
import { useProfileDispatch } from '../../contexts/profile.context'
import { useProductOptionSequenceDispatch } from '../../contexts/productOptionSequence.context'
import {
  useIsLocationUpdatedContext,
  useIsLocationUpdatedDispatch,
  useLocationContext,
  useLocationDispatch,
} from '../../contexts/location.context'
import OverlaySearchProvider from '../../contexts/overlaySearch.context'
import usePLAController from '../../controllers/pla.controller'
import useCartController from '../../controllers/cart.controller'
import { LocalContext } from '../../helpers/useLocalContext'
import { GTM } from '../../helpers'
import { getSyncSEDetails } from '../../helpers/getShippingExpectationDate'
import getAccessToken from '../../helpers/getAccessToken'
import addProduct from '../../helpers/addToCart'
import notifyMe from '../../helpers/NotifyMe'
import getCityDetails from '../../helpers/getCityDetails'
import getInventoryDetails from '../../helpers/getInventoryDetails'
import TopBarFilterOptions from './TopBarFilterOptions'
import ProductCard from './mainSectionComponents/ProductCard'
import { SearchContext } from '../common/context/SearchContextProvider'
import { usePLAContext } from '../../contexts/pla.context'
import { useShoppingCartContext } from '../ShoppingCartV2/providers/ShoppingCartProvider'
import useUpdateCartItemsShippingDetails from '../../helpers/updateCartItemsShippingDetails'

const defaultSearchString = ''
const itemPerPage = 'itemperpage'
const defaultPageItems = 15
const currentPage = 'currentpage'
const defaultCurrentPage = '1'
let isShowHeight = false

export default function MainSection(props) {
  const {
    toggleItemsPerPage,
    removePrice,
    pageType,
    coreConfig,
  } = props

  const {
    searchState: { products, isSearch },
  } = useContext(SearchContext)

  const { isDesktop } = useResponsiveWrapperContext()
  const isLocationUpdated = useIsLocationUpdatedContext()
  const setIsLocationUpdated = useIsLocationUpdatedDispatch()
  const updateCartItemsShippingDetails = useUpdateCartItemsShippingDetails()
  const [isLoading, setLoader] = useState(false)
  const [zipcodeError, setZipcodeError] = useState(false)
  const [invDetails, setInvDetails] = useState({})

  const {
    items: productItems = [],
    resultCount,
    pagination,
    isVehicleFit,
  } = products
  const { localContext } = useContext(LocalContext)
  const { history, isBot } = localContext
  const { siteSearch, blueshiftSearch } = GTM
  const urlParams = useMemo(
    () => new URLSearchParams(
      __BROWSER__
        ? window.location.search
        : history.location.pathname,
    ),
    [history.location.pathname],
  )

  const pageItemsCount = Number(urlParams.get(itemPerPage) || defaultPageItems)
  const pageNumber = Number(urlParams.get(currentPage) || defaultCurrentPage)
  const pla = usePLAContext()

  const cartState = useCartContext()
  const vehicleState = useVehicleContext()
  const tokenDB = useTokenContext()
  const locationState = useLocationContext()
  const setTokenDB = useTokenDispatch()
  const setProfileData = useProfileDispatch()
  const setProductOptionSequence = useProductOptionSequenceDispatch()
  const setLocationData = useLocationDispatch()
  const { addCart, clearCart } = useCartController()
  const { updatePLA } = usePLAController()

  const clearFromPDPPLA = () => {
    updatePLA({ fromPDPPLA: {} })
  }
  const brand = pla?.fromPDPPLA?.brand
  const zipCode = (locationState || {}).postalCode
  const cityName = (locationState || {}).city
  const boxWidth = isDesktop ? '33.33%' : '100%'

  const { getCartId } = useShoppingCartContext()
  const cartId = getCartId()


  const addToCart = async productToAdd => {
    let selectedVehicle = {}
    let orderId = cartState && cartState.orderId

    if (brand) clearFromPDPPLA()

    const vehicleDBInstance = (vehicleState && vehicleState.vehicleDB) || []
    selectedVehicle = vehicleDBInstance.find(element => element.selected) || {}
    const hasAccessToken = tokenDB && tokenDB.access_token
    let newTokenDB = tokenDB

    if (!hasAccessToken) {
      newTokenDB = await getAccessToken()
      setTokenDB(newTokenDB)
    }

    addProduct(productToAdd, '1', selectedVehicle, orderId, newTokenDB, false, zipCode, cartId)
      .then(({ data: responseData }) => {
        if (!orderId) {
          orderId = typeof responseData.results === 'string'
            ? responseData.results
            : responseData.results[0]
        }
        if (
          responseData
          && responseData.results
          && responseData.results.order
          && responseData.results.order.newOrderId
        ) {
          orderId = responseData.results.order.newOrderId
        }
        // eslint-disable-next-line no-restricted-globals
        if (isNaN(orderId)) throw new Error('Order Id is invalid.')

        let qty = cartState && cartState.cartQuantity
          ? parseInt(cartState.cartQuantity, 10)
          : 0
        qty += 1
        addCart({
          orderId,
          cartQuantity: qty,
        })

        return new Promise(resolve => {
          setTimeout(() => resolve(), 200)
        })
      })
      .then(() => history.push('/cart'))
      .catch(error => {
        if (error.response.status === 423) {
          setTokenDB({})
          setProfileData({})
          clearCart()
          setProductOptionSequence({})
        }
        throw error
      })
  }

  const vendorRedirect = url => {
    history.push(url)
  }

  const onClickNotify = (email, pid, captcha) => notifyMe(email, pid, captcha)

  const zipCodeHandler = postalCode => {
    if (postalCode) {
      setLoader(true)
      setZipcodeError(false)
      return getCityDetails(postalCode)
        .then(locationData => {
          setLocationData({
            postalCode,
            city: locationData.city,
            state: locationData.region,
          })
          updateCartItemsShippingDetails({
            location: {
              zipcode: postalCode,
              city: locationData.city,
              state: locationData.region
            }
          })
          setIsLocationUpdated(true)
          return true
        })
        .catch(() => {
          setZipcodeError(true)
          return false
        })
        .finally(() => {
          setLoader(false)
        })
    }
    return Promise.resolve(false)
  }

  const getDeliveryDate = index => {
    setLoader(true)
    return getSyncSEDetails(productItems[index], false).then(res => {
      setLoader(false)
      return res
    })
  }

  const showFree = useCallback(
    (productRecords, recordIndex) => {
      if (isDesktop) {
        const totalLength = productRecords.length
        if (recordIndex < totalLength) {
          const item = productRecords[`${recordIndex}`]
          const { isFreeShipping: showHeight = false, available } = invDetails[
            item.id
          ] || {}

          if (showHeight && available) {
            return showHeight
          }
        }
        if (recordIndex + 1 < totalLength) {
          const item = productRecords[recordIndex + 1]
          const { isFreeShipping: showHeight = false, available } = invDetails[
            item.id
          ] || {}

          if (showHeight && available) {
            return showHeight
          }
        }
        if (recordIndex + 2 < totalLength) {
          const item = productRecords[recordIndex + 2]
          const { isFreeShipping: showHeight = false, available } = invDetails[
            item.id
          ] || {}

          if (showHeight && available) {
            return showHeight
          }
        }
      } else {
        const item = productRecords[`${recordIndex}`]
        const { isFreeShipping: showHeight = false, available } = invDetails[
          item.id
        ] || {}
        if (showHeight && available) {
          return showHeight
        }
      }
    },
    [isDesktop, invDetails],
  )

  useEffect(() => {
    let mounted = true

    if (productItems && productItems.length) {
      const pids = productItems.map(item => item.id).join('|')
      getInventoryDetails(pids, true).then(inventoryDetails => {
        if (!mounted) return
        setInvDetails(inventoryDetails)
      })
    }

    return () => {
      mounted = false
    }
  }, [isVehicleFit, productItems])

  useEffect(() => {
    if (isSearch && productItems && productItems.length > 0) {
      const searchString = (urlParams.get('q') || defaultSearchString)
        .replace(/_/gi, ' ')
        .trim()

      if (searchString) {
        siteSearch(searchString, resultCount)

        blueshiftSearch({
          email: undefined,
          items: productItems.map(product => product.sku),
        })
      }
    }
  }, [
    blueshiftSearch,
    siteSearch,
    productItems,
    resultCount,
    isSearch,
    urlParams,
  ])

  const hasProductItems = (productItems && productItems.length > 0) || productItems !== null
  const noProductItems = Boolean(!hasProductItems)

  const initialStyleMemo = useMemo(() => ({ width: boxWidth }), [boxWidth])
  const lazyLoad = useMemo(() => !isBot && isSearch, [isBot, isSearch])

  return noProductItems ? (
    <Box>No product found</Box>
  ) : (
    <OverlaySearchProvider overlayItems={productItems}>
      {/* Top Bar Filter ->
           Contains information about search product
           Filtes like
            1. product per page
            2. sorting
        */}
      <ResponsiveBox
        id="topBarFilterWrapper"
        fxMargin={{ large: { bottom: 'medium' } }}
      >
        {hasProductItems && (
          <TopBarFilterOptions toggleItemsPerPage={toggleItemsPerPage} />
        )}
      </ResponsiveBox>

      {/* Product Cards section -> contains information about product */}
      <Box id="MainSectionGrid" direction="row" wrap>

        {/* Product Card has actual information related to searched product */}
        {hasProductItems
          && productItems.map((element, index) => {
            if (Object.keys(invDetails).length && index % 3 === 0) {
              isShowHeight = Boolean(showFree(productItems, index))
            }
            const mapKey = `product-${element.id}-${index}`

            return (

              <Hydrator
                Component={ProductCard}
                lazy={lazyLoad}
                initialStyle={initialStyleMemo}
                boxWidth={boxWidth}
                key={mapKey}
                cardId={seoIDs.sectionCard.replace('{{key}}', index)}
                index={index}
                data={element}
                addToCart={addToCart}
                vendorRedirect={vendorRedirect}
                onClickNotify={onClickNotify}
                invDetails={invDetails}
                cityName={cityName}
                zipCode={zipCode}
                zipCodeHandler={zipCodeHandler}
                zipcodeError={zipcodeError}
                isLoading={isLoading}
                getDeliveryDate={getDeliveryDate}
                removePrice={removePrice}
                isShowHeight={isShowHeight}
                pageType={pageType}
                coreConfig={coreConfig}
                isLocationUpdated={isLocationUpdated}
                setIsLocationUpdated={setIsLocationUpdated}
              />
            )
          })}
      </Box>

      {/* Pagination Section -> pagination filter */}
      <Box justify="center">
        <Pagination
          totalPages={pagination.numberOfPages}
          pageNumber={pageNumber}
          shownRecords={
            pageItemsCount > resultCount ? resultCount : pageItemsCount
          }
          totalRecords={resultCount}
          commonSize="40px"
          callback={updatedCurrentPage => toggleItemsPerPage('currentpage', updatedCurrentPage)}
          GTM={GTM}
          caller="search"
        />
      </Box>
    </OverlaySearchProvider>
  )
}
